import React from 'react'
import { Box, Typography } from '@mui/material'
import { ComputerDesktopIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { DefaultModal } from '../../../shared/components/DefaultModal'
import { Button } from '../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../constants/colors'
import { TESTIDS } from '../../../config/testids'

interface GameDesktopOnlyModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * GameDesktopOnlyModal - Blocking notice shown on phones/tablets when the user tries
 * to join a GAME activity (Whereby lobby + Unity game). Game activities are desktop-only;
 * the modal never renders for desktop users and never blocks non-game activities.
 */
export const GameDesktopOnlyModal: React.FC<GameDesktopOnlyModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()

  return (
    <DefaultModal isOpen={isOpen} onClose={onClose} maxWidth="440px">
      <Box
        data-testid={TESTIDS.GAME_DESKTOP_ONLY_MODAL}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: { xs: 2, sm: 2.5, md: 3 },
          p: { xs: 1, sm: 1.5, md: 2 }
        }}
      >
        {/* Icon: phone → desktop */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 2,
            borderRadius: '999px',
            backgroundColor: INTERAX_COLORS.sun20
          }}
        >
          <DevicePhoneMobileIcon style={{ width: '28px', height: '28px', color: INTERAX_COLORS.thunderGrey }} />
          <Typography component="span" sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '18px', lineHeight: 1 }}>
            →
          </Typography>
          <ComputerDesktopIcon style={{ width: '32px', height: '32px', color: INTERAX_COLORS.sun }} />
        </Box>

        <Typography variant="h6" sx={{ color: INTERAX_COLORS.white, fontWeight: 600 }}>
          {t('gamer.desktopOnly.title')}
        </Typography>

        <Typography variant="body1" sx={{ color: INTERAX_COLORS.typographyFont, lineHeight: 1.6 }}>
          {t('gamer.desktopOnly.message')}
        </Typography>

        <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('gamer.desktopOnly.hint')}
        </Typography>

        <Button variant="primary" onClick={onClose} fullWidth data-testid={TESTIDS.GAME_DESKTOP_ONLY_CONFIRM_BTN}>
          {t('gamer.desktopOnly.confirm')}
        </Button>
      </Box>
    </DefaultModal>
  )
}
